// src/common/NotificationItem/index.js

import React, { forwardRef, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { MenuItem, Icon } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import styles from "./styles";

const NotificationItem = forwardRef(
  ({ color = "dark", icon, title, message, timeAgo, read, id, onMarkAsRead, ...rest }, ref) => {
    const classes = styles({ color });
    const hoverTimeoutRef = useRef(null);

    const handleMouseEnter = useCallback(() => {
      if (!read && onMarkAsRead) {
        // Añadir delay para evitar marcados accidentales
        hoverTimeoutRef.current = setTimeout(() => {
          onMarkAsRead(id);
        }, 500);
      }
    }, [read, onMarkAsRead, id]);

    const handleMouseLeave = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    }, []);

    return (
      <MenuItem
        {...rest}
        ref={ref}
        className={classes.notificationItem}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          backgroundColor: read ? "transparent" : "action.hover",
          "&:hover": { backgroundColor: "action.selected" },
          transition: "background-color 0.2s ease",
        }}
      >
        <SuiBox customClass={classes.notificationItem_icon}>
          <Icon sx={{ color: `${color}.main` }}>{icon}</Icon>
        </SuiBox>
        <SuiBox>
          <SuiTypography
            variant="button"
            fontWeight={read ? "regular" : "medium"}
            textTransform="capitalize"
            mb={0.5}
          >
            {title}
          </SuiTypography>
          <SuiTypography variant="caption" textColor="secondary" display="block">
            {message}
          </SuiTypography>
          <SuiTypography
            variant="caption"
            textColor="secondary"
            display="flex"
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Icon fontSize="small" sx={{ mr: 0.5 }}>
              watch_later
            </Icon>
            {timeAgo}
          </SuiTypography>
        </SuiBox>
      </MenuItem>
    );
  }
);

NotificationItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  timeAgo: PropTypes.string.isRequired,
  read: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onMarkAsRead: PropTypes.func,
};

NotificationItem.displayName = "NotificationItem";

export default NotificationItem;
